import React from "react";
import type { AppPreferencesContextType } from "../types/AppPreferencesProvider";

// `userActionsEnum` and `AppPreferencesContext` live in this standalone module
// (rather than in `AppPreferencesProvider`) so they can be imported without
// pulling in `AppPreferencesProvider`'s heavy transitive dependencies —
// notably `ThirdPartyProvider`, which runs `initAllNonAuthenticatedServices()`
// at module load. The shared test/story render harness imports from here.
// `AppPreferencesProvider` re-exports both for backward compatibility, so
// existing `from ".../AppPreferencesProvider"` import sites keep working.

export const userActionsEnum = {
  EDIT_SCOPE: "scopes_edit",
  SOFT_EDIT_SCOPE: "scopes_soft_edit",
  CREATE_SCOPE: "scopes_create",
  DELETE_SCOPE: "scopes_delete",
  EDIT_FILTER: "filters_edit",
  CREATE_FILTER: "filters_create",
  SELF_EDIT_FILTER: "filters_edit_self",
  SELF_DELETE_FILTER: "filters_delete_self",
  DELETE_FILTER: "filters_delete",
  MANAGE_USERS: "user_management",
  EDIT_USER_PROFILE: "user_profile_edit",
  MANAGE_PERMISSIONS: "permissions_management",
  VIEW_WORKFLOWS: "workflow_view",
  MANAGE_SAML: "saml_settings",
  EDIT_SCORING_SETTINGS: "scoring_rule_edit",
  VIEW_SUB_STATUSES_RULES: "settings_status_view",
  EDIT_SUB_STATUSES_RULES: "settings_status_edit",
  EDIT_SLA_SETTINGS: "settings_sla_rules_edit",
  CREATE_FINDING_COMMENT: "finding_comment_create",
  DELETE_FINDING_COMMENT: "finding_comment_delete",
  ADD_FINDINGS: "create_manual_finding",
  DOWNLOAD_FINDINGS_LIST_CSV: "download_findings_csv",
  ACT_ON_FINDINGS: "finding_actions",
  ADD_FINDING_ATTACHMENT: "finding_attachments_add",
  DELETE_FINDING_ATTACHMENT: "finding_attachments_delete",
  ACT_ON_RESOURCES: "resource_actions",
  CREATE_RESOURCE_TAGS: "resource_tags_create",
  VIEW_DATASOURCES_SETTINGS: "settings_datasources_view",
  EDIT_DATASOURCES_SETTINGS: "settings_datasources_edit",
  VIEW_API_TOKENS_SETTINGS: "api_management",
  DELETE_API_TOKEN: "api_token_delete",
  CREATE_API_TOKEN: "api_token_create",
  EDIT_TICKETING_SYSTEMS_SETTINGS: "settings_ticketing_systems_edit",
  FINDING_STATUS_VIEW: "finding_status_view",
  FINDING_STATUS_EDIT: "finding_status_edit",
  FINDING_SCORE_EDIT: "finding_score_edit",
  FINDING_SLA_EDIT: "finding_user_sla_edit",
  EDIT_FINDINGS_STATUS_RULES: "finding_status_rule_edit",
  CREATE_FINDING_TICKET: "open_ticket",
  DETACH_FINDING_TICKET: "detach_ticket",
  CREATE_RMQ: "remediation_queue_create",
  DELETE_RMQ: "remediation_queue_delete",
  EDIT_RMQ: "remediation_queue_edit",
  DELETE_DASHBOARD: "dashboard_delete",
  EDIT_DASHBOARD: "dashboard_edit",
  CREATE_DASHBOARD: "dashboard_create",
  EDIT_SELF_DASHBOARD: "dashboard_edit_self",
  DELETE_SELF_DASHBOARD: "dashboard_delete_self",
  SET_DEFAULT_DASHBOARD: "dashboard_set_default",
  EXPORT_DASHBOARD: "dashboard_export",
  PRIORITIZATION_VIEW: "settings_prioritization_edit",
  VIEW_UNSCOPED_RESOURCES: "view_unscoped_resources",
  EDIT_FINDINGS_AGGREGATION_RULES: "findings_aggregate",
  DELETE_REMOTE_COLLECTOR_INSTANCE: "settings_remote_collector_delete_instance",
  DELETE_DATASOURCE_INSTANCE: "settings_datasources_delete_instance",
  EDIT_AI_CONFIGURATION: "settings_ai_configuration_edit",
  VIEW_RULES_PAGE: "settings_rules_view",
  VIEW_SEEM_CHAT: "view_ai_chat",
  RUN_AGENT_ANALYSIS: "run_agent_analysis",
  CREATE_ANALYST_AUTOMATION: "analyst_automation_create",
  EDIT_ANALYST_AUTOMATION: "analyst_automation_edit",
  DELETE_ANALYST_AUTOMATION: "analyst_automation_delete",
} as const;

export const AppPreferencesContext = React.createContext(
  {} as AppPreferencesContextType
);
